import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Box,
	Typography,
	Checkbox,
	FormControlLabel,
} from "@mui/material";

import DropdownController from "../../components/inputs/DropdownController";
import TextFieldController from "../../components/inputs/TextFieldController";
import PATCloseButton from "../../components/Buttons/PATCloseButton";
import PATSaveButton from "../../components/Buttons/PATSaveButton";
import { getCodeDescription, getCodeId, maxLengthMessage } from "../../utils/functions";
import { useSelector } from "react-redux";
import { findEmployees } from "../../utils/commonServices";
import { encrypt } from "../../utils/encryptionDecryptionHelper";

const checkoutSchema = yup.object().shape({
	perPersonnelId: yup.string().nullable(),
	division: yup.string().trim().max(50, maxLengthMessage(50)).nullable(),
	odometer: yup
		.number()
		.nullable()
		.transform((v, o) => (o === "" ? null : v)),
	checkoutNotes: yup
		.string()
		.trim()
		.max(500, maxLengthMessage(500))
		.nullable(),
	performInspection: yup.boolean(),
});

const buildDefaults = (vehicle) => ({
	perPersonnelId: "",
	division: vehicle?.division || "",
	odometer: "",
	checkoutNotes: "",
	performInspection: false,
});

const CheckInOutVehicleDialog = ({
	open,
	onClose,
	onSave,
	vehicle,
	checkoutMode = "out",
	employees = [],
	divisionOptions = [],
	canPerformInspection = false,
}) => {
	const isCheckIn = checkoutMode === "in";
	const dictionary = useSelector((state) => state.dictionary.dictionary);

	const [selectedEmployee, setSelectedEmployee] = React.useState(null);

	const {
		control,
		handleSubmit,
		reset,
		watch,
		setValue,
		formState: { errors },
	} = useForm({
		resolver: yupResolver(checkoutSchema),
		defaultValues: buildDefaults(vehicle),
		mode: "onChange",
	});

	// Reset form when dialog opens
	useEffect(() => {
		if (open) {
			reset(buildDefaults(vehicle));
			setSelectedEmployee(null);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, vehicle, reset]);

	// Reset on close
	useEffect(() => {
		if (!open) {
			reset();
			setSelectedEmployee(null);
		}
	}, [open, reset]);

	const handleClose = () => {
		reset();
		setSelectedEmployee(null);
		onClose && onClose();
	};

	const onSubmit = (data) => {
		let payload;

		if (isCheckIn) {
			payload = {
				perPersonnelId: null,
				odometer: data?.odometer || null,
				notes: data?.checkoutNotes?.trim() || null,
			};
		} else {
			payload = {
				perPersonnelId: parseInt(data?.perPersonnelId) || null,
				badgeNum: selectedEmployee?.badgeNum || "",
				odometer: data?.odometer || null,
				division: data?.division || null,
				divisionId: getCodeId(
					data?.division,
					dictionary.codePerDepartment,
				),
				notes: data?.checkoutNotes?.trim() || null,
			};
		}

		// Include inspection data if checkbox is checked
		const inspectionData = data?.performInspection
			? {
					performInspection: true,
					inspectionPreFill: {
						vehicleId: vehicle?.id,
						perPersonnelId: isCheckIn
							? vehicle?.perPersonnelId // Use current assignee for check-in
							: parseInt(data?.perPersonnelId) || null,
						odometer: data?.odometer || vehicle?.odometer || null,
						// Include vehicle details for display
						vehicleInfo: {
							unitNumber: vehicle?.unitNumber,
							make: vehicle?.make,
							model: vehicle?.model,
							year: vehicle?.year,
							status: vehicle?.status,
						},
					},
				}
			: { performInspection: false };

		onSave(vehicle?.id, payload, inspectionData, vehicle);
		handleClose();
	};

	if (!vehicle) return null;

	// eslint-disable-next-line eqeqeq
	const curEmp = employees.find((e) => e.value == vehicle.perPersonnelId);
	const perPersonnelId = watch("perPersonnelId");
	const odometer = watch("odometer");
	const performInspection = watch("performInspection");

	const mileageWarning =
		odometer && vehicle.odometer && +odometer < vehicle.odometer;

	const mileageUpdate = odometer && +odometer > (vehicle.odometer || 0);

	const isSaveDisabled = !isCheckIn && !perPersonnelId;

	const dialogTitle = isCheckIn
		? `Check In — ${vehicle.unitNumber}`
		: `Check Out — ${vehicle.unitNumber}`;

	const handleChangeEmployee = async (value) => {
		if (!value) {
			setSelectedEmployee(null);
			return;
		}

		try {
			const selEmp = await findEmployees({
				$limit: -1,
				id: encrypt(value),
			});
			setSelectedEmployee(selEmp[0] || null);
		} catch (error) {
			console.error("Error fetching employee details:", error);
			setSelectedEmployee(null);
		}
	};

	return (
		<Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
			<DialogTitle>{dialogTitle}</DialogTitle>

			<DialogContent>
				<Box
					component="form"
					onSubmit={handleSubmit(onSubmit)}
					noValidate
					sx={{
						display: "flex",
						flexDirection: "column",
						gap: 2,
						pt: 1,
					}}
				>
					{/* Vehicle Summary */}
					<Box
						sx={{
							display: "flex",
							gap: 1.5,
							p: 1.5,
							border: "1px solid",
							borderColor: "divider",
							borderRadius: 1,
							alignItems: "center",
							bgcolor: "background.default",
						}}
					>
						<Typography sx={{ fontSize: 22 }}>🚗</Typography>
						<Box>
							<Typography variant="body2" fontWeight={800}>
								{vehicle.unitNumber} — {vehicle.year}{" "}
								{vehicle.make} {vehicle.model}
							</Typography>
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 1,
									mt: 0.25,
								}}
							>
								<Typography
									variant="caption"
									color="text.secondary"
								>
									{getCodeDescription(vehicle.division, dictionary.codePerDepartment) || "—"}
								</Typography>
								{vehicle.status && (
									<Typography
										variant="caption"
										fontWeight={700}
										sx={{
											px: 1,
											borderRadius: 10,
											fontSize: 11,
											bgcolor:
												vehicle["statusInfo.InService"] === true
													? "rgba(34,197,94,0.12)"
													: "rgba(239,68,68,0.1)",
											color:
												vehicle["statusInfo.InService"] === true
													? "success.main"
													: "error.main",
										}}
									>
										{getCodeDescription(vehicle.status, dictionary.codePerVehicleStatus) || "—"}
									</Typography>
								)}
							</Box>
						</Box>
					</Box>

					{/* Currently Assigned To */}
					<Box
						sx={{
							p: 1.5,
							bgcolor: curEmp
								? "rgba(234,179,8,0.08)"
								: "background.default",
							border: "1px solid",
							borderColor: curEmp
								? "rgba(234,179,8,0.3)"
								: "divider",
							borderRadius: 1,
						}}
					>
						<Typography
							variant="caption"
							fontWeight={700}
							color="text.secondary"
							sx={{
								textTransform: "uppercase",
								letterSpacing: "0.06em",
								mb: 0.5,
								display: "block",
							}}
						>
							Currently Assigned To
						</Typography>
						{curEmp ? (
							<Typography variant="body2" fontWeight={600}>
								{curEmp.label || "-"}
								<Typography
									component="span"
									variant="caption"
									color="text.secondary"
									fontFamily="monospace"
									sx={{ ml: 1 }}
								>
									{curEmp.badge}
								</Typography>
							</Typography>
						) : (
							<Typography
								variant="caption"
								color="text.disabled"
								sx={{ fontStyle: "italic" }}
							>
								Unassigned
							</Typography>
						)}
					</Box>

					{/* Check Out To (checkout only) */}
					{!isCheckIn && (
						<Box>
							<DropdownController
								name="perPersonnelId"
								label="Check Out To *"
								options={employees}
								onChange={(value) => {
									handleChangeEmployee(value);
								}}
								control={control}
								errors={errors}
							/>
							{selectedEmployee && (
								<Box
									sx={{
										mt: 1,
										p: 1,
										bgcolor: "rgba(99,102,241,0.06)",
										border: "1px solid rgba(99,102,241,0.2)",
										borderRadius: 1,
									}}
								>
									<Typography
										variant="caption"
										fontWeight={600}
									>
										{selectedEmployee?.rank}{" "}
										{selectedEmployee?.firstName}{" "}
										{selectedEmployee?.lastName}
									</Typography>
									<Typography
										variant="caption"
										color="text.secondary"
										sx={{ ml: 0.75 }}
									>
										{selectedEmployee?.department || ""}
									</Typography>
								</Box>
							)}
						</Box>
					)}

					{/* Division (checkout only) */}
					{!isCheckIn && (
						<Box>
							<DropdownController
								name="division"
								label="Division"
								options={divisionOptions}
								control={control}
								errors={errors}
							/>
						</Box>
					)}

					{/* Mileage */}
					<Box>
						<TextFieldController
							name="odometer"
							label="Odometer Reading (mi) — optional"
							control={control}
							errors={errors}
							type="number"
							placeholder={
								vehicle.odometer
									? vehicle.odometer.toLocaleString()
									: "Enter mileage..."
							}
						/>
						{mileageWarning && (
							<Typography
								variant="caption"
								color="error"
								fontWeight={700}
								sx={{ mt: 0.5, display: "block" }}
							>
								⚠ Entered mileage is less than current odometer
								({vehicle.odometer?.toLocaleString()} mi)
							</Typography>
						)}
						{mileageUpdate && (
							<Typography
								variant="caption"
								color="success.main"
								sx={{ mt: 0.5, display: "block" }}
							>
								✓ Vehicle odometer will be updated to{" "}
								{(+odometer).toLocaleString()} mi
							</Typography>
						)}
					</Box>

					{/* Check-in confirmation message */}
					{isCheckIn && (
						<Box
							sx={{
								p: 1.5,
								bgcolor: "rgba(34,197,94,0.06)",
								border: "1px solid rgba(34,197,94,0.2)",
								borderRadius: 1,
							}}
						>
							<Typography
								variant="caption"
								color="text.secondary"
							>
								This will clear the current assignment and mark{" "}
								{vehicle.unitNumber} as unassigned.
							</Typography>
						</Box>
					)}

					{/* Notes */}
					<Box>
						<TextFieldController
							name="checkoutNotes"
							label="Notes (optional)"
							control={control}
							errors={errors}
							multiline
							rows={2}
							placeholder={
								isCheckIn
									? "Condition notes, reason for check-in..."
									: "Purpose, shift, notes..."
							}
						/>
					</Box>

					{/* Perform Inspection Toggle */}
					{canPerformInspection && (
						<Box
							sx={{
								p: 1.5,
								bgcolor: performInspection
									? "rgba(99,102,241,0.08)"
									: "background.default",
								border: "1px solid",
								borderColor: performInspection
									? "rgba(99,102,241,0.3)"
									: "divider",
								borderRadius: 1,
								cursor: "pointer",
								userSelect: "none",
								transition: "all 0.2s ease",
							}}
							onClick={() =>
								setValue(
									"performInspection",
									!performInspection,
								)
							}
						>
							<FormControlLabel
								control={
									<Checkbox
										checked={performInspection || false}
										onChange={(e) =>
											setValue(
												"performInspection",
												e.target.checked,
											)
										}
										size="small"
										color="primary"
									/>
								}
								label={
									<Box>
										<Typography
											variant="body2"
											fontWeight={700}
											sx={{ fontSize: 13 }}
										>
											Perform Inspection
										</Typography>
										<Typography
											variant="caption"
											color="text.secondary"
										>
											Launch inspection wizard after
											confirming{" "}
											{isCheckIn
												? "check-in"
												: "check-out"}
										</Typography>
									</Box>
								}
							/>
						</Box>
					)}

					{/* Info message when inspection is selected */}
					{performInspection && (
						<Box
							sx={{
								p: 1.5,
								bgcolor: "rgba(99,102,241,0.04)",
								border: "1px solid rgba(99,102,241,0.15)",
								borderRadius: 1,
							}}
						>
							<Typography variant="caption" color="primary">
								📋 After {isCheckIn ? "check-in" : "check-out"},
								the inspection wizard will open with vehicle and
								{!isCheckIn
									? " selected employee"
									: " current assignee"}{" "}
								pre-filled.
							</Typography>
						</Box>
					)}
				</Box>
			</DialogContent>

			<DialogActions sx={{ px: 3, pb: 2 }}>
				<PATSaveButton
					onClick={handleSubmit(onSubmit)}
					disabled={isSaveDisabled}
					tooltipText={
						performInspection
							? `${isCheckIn ? "Check In" : "Check Out"} & Start Inspection`
							: isCheckIn
								? "Confirm Check In"
								: "Confirm Check Out"
					}
				/>
				<PATCloseButton onClick={handleClose} tooltipText="Cancel" />
			</DialogActions>
		</Dialog>
	);
};

export default CheckInOutVehicleDialog;
